/**
 * The note endpoints, as the frontend sees them (PHASE-0.md §2.5).
 *
 * Mirrors `api/src/Continuum.Api/Notes/NoteEndpoints.cs` and the codes
 * `NoteContent.Validate` and `NoteWriteService` return.
 *
 * **Two writes, not three.** Writing and signing are one request because they are one
 * transaction on the server: there is no endpoint that stores an unsigned note, and
 * that is the schema's whole point rather than an omission. A correction is the second
 * call, and it posts to the note's `/versions` collection.
 *
 * **And one read**, {@link fetchNoteVersions}, which is what §2.5's *"en rättelse skapar
 * en ny version med den gamla synlig"* actually needs: the patient overview shows the
 * current version of each note, and the old ones are only visible if something asks for
 * them. It is also where a correction gets its `supersedes` — the overview's note list
 * carries `version` but not `version_id`, deliberately, because a number is what a reader
 * needs and the row identity is what a writer needs.
 *
 * **Nothing here persists a draft.** No `localStorage`, no IndexedDB, no beacon on
 * unload — DECISIONS.md #38. A draft lives in React state and dies with the component,
 * and the editor says so on screen. An unsigned note that survived a reload would be
 * clinical text in a place the audit log does not describe and no `DELETE` can clear.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/** What the two writes send. Mirrors `NoteRequest`. */
export interface NoteSubmission {
  /** Local note type as a code — `daily`, `admission`, `discharge`. */
  readonly noteType: string

  readonly subjective: string | null
  readonly objective: string | null
  readonly assessment: string | null
  readonly plan: string | null
  readonly bodyText: string | null

  /**
   * BCP 47 subtag the text **is written in** — not the interface language.
   *
   * The server defaults it to `sv` when absent, and this sends it explicitly anyway:
   * the column decides whether the text may ever be machine-translated (AGENTS.md §3
   * rule 8), so the client should not rely on a default for it.
   */
  readonly language: string
}

/** What a successful write returns. Mirrors `NoteWritten`. */
export interface NoteWritten {
  readonly noteId: string

  /**
   * The version just written. **A correction must name this as its `supersedes`**, which
   * is why it is returned rather than looked up again.
   */
  readonly versionId: string

  readonly version: number

  /** `signed` for a first version, `amended` for a correction. */
  readonly status: string

  /** When the content is clinically true. A correction inherits version 1's. */
  readonly effectiveAt: string

  /** When this version was signed. A correction gets its own. */
  readonly signedAt: string

  readonly signatureId: string
}

/**
 * Every code the two writes can refuse with.
 *
 * Listed rather than left as `string` so that a code added on the server without a
 * catalogue entry here is a typecheck failure and not a blank message on the one screen
 * where the clinician has just lost what they typed. `noteRefusalKey` maps each to a
 * catalogue key, and the compiler holds that map exhaustive.
 */
export const NOTE_REFUSAL_CODES = [
  'NOTE_BODY_MISSING',
  'NOTE_TYPE_MISSING',
  'NOTE_TYPE_INVALID',
  'NOTE_EMPTY',
  'NOTE_FIELD_TOO_LONG',
  'NOTE_LANGUAGE_INVALID',
  'NOTE_SUPERSEDES_MISSING',
  'NOTE_SUPERSEDES_UNEXPECTED',
  'NOTE_VERSION_STALE',
  'NOTE_RETRACTED',
  'NOTE_NOT_FOUND',
  'PATIENT_NOT_FOUND',
] as const

export type NoteRefusalCode = (typeof NOTE_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface NoteRefusalParams {
  /** `NOTE_FIELD_TOO_LONG`: which box to shorten, by its submission field name. */
  readonly field?: string

  /** `NOTE_FIELD_TOO_LONG` and `NOTE_TYPE_INVALID`: the limit. */
  readonly maximum?: number

  /** `NOTE_VERSION_STALE`: the version the client believed it was correcting. */
  readonly supersedes?: string

  /**
   * `NOTE_VERSION_STALE`: the version that is current instead.
   *
   * **Absent when two corrections raced**, rather than wrong — the server's transaction
   * rolled back and cannot say what won without a second read. The screen refetches, so
   * it must not depend on this being here.
   */
  readonly current?: string

  readonly patientId?: string
  readonly noteId?: string
}

/**
 * A write the server refused, with the code it refused by.
 *
 * Distinct from {@link NoteWriteUnavailableError} because the two mean opposite things
 * to a clinician holding unsaved text: a refusal is something to fix in the form, and an
 * unavailable API is something to wait out **without closing the editor**.
 */
export class NoteRefusedError extends Error {
  readonly code: NoteRefusalCode | 'UNKNOWN'
  readonly params: NoteRefusalParams
  readonly status: number

  constructor(code: NoteRefusalCode | 'UNKNOWN', params: NoteRefusalParams, status: number) {
    super(`Note write refused: ${code}`)
    this.name = 'NoteRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class NoteWriteUnavailableError extends Error {
  constructor() {
    super('Note write unavailable')
    this.name = 'NoteWriteUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is NoteRefusalCode {
  return (
    typeof value === 'string' &&
    (NOTE_REFUSAL_CODES as readonly string[]).includes(value)
  )
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable or unrecognised body still becomes a {@link NoteRefusedError} with code
 * `UNKNOWN` rather than an unavailable error, because the server did answer: the write
 * did not happen, and telling the clinician to wait would be wrong.
 */
async function refusalFrom(response: Response): Promise<NoteRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: NoteRefusalParams }
    }
    const code = body.error?.code

    return new NoteRefusedError(
      isRefusalCode(code) ? code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new NoteRefusedError('UNKNOWN', {}, response.status)
  }
}

async function post(
  url: string,
  body: unknown,
  signal: AbortSignal,
): Promise<NoteWritten> {
  let response: Response

  try {
    response = await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request. Re-thrown so the
    // caller can tell it from a failure — but note that a write which reached the
    // server still happened, which is why the editor does not abort a submit in
    // flight. See `NoteEditor`.
    if (signal.aborted) {
      throw cause
    }
    throw new NoteWriteUnavailableError()
  }

  // Before `refusalFrom`, which reads the body — and before it for the reason as well as the
  // order: a 401 is not a refusal of this write, it is the session having ended, and
  // `SessionGate` is about to show the login screen. Reported rather than rendered — see
  // `auth/sessionExpiry.ts`. The throw still happens, so the editor does not treat an
  // unanswered write as a success in the moment before it unmounts. A note typed into a
  // session that expired is lost either way; what must not happen is the clinician being told
  // their note was refused for its content when it was not written at all.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as NoteWritten
  } catch {
    // A 201 whose body did not parse is the worst of the failure modes: the note is in
    // the record and this client cannot say which version it is. Reported as
    // unavailable, and the editor's success path is not taken — the clinician refetches
    // and sees their note rather than being told it was lost.
    throw new NoteWriteUnavailableError()
  }
}

/**
 * Writes and signs a first version.
 *
 * **Not idempotent, and not retryable on a timeout.** There is no client-supplied id to
 * deduplicate on, so a retry after an uncertain outcome writes a second note that no
 * `DELETE` can remove (AGENTS.md §3). The editor offers a refetch, never an automatic
 * retry.
 */
export async function signNote(
  patientId: string,
  submission: NoteSubmission,
  signal: AbortSignal,
): Promise<NoteWritten> {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/notes`,
    submission,
    signal,
  )
}

/**
 * Corrects a note: a new version, signed, with the old one still readable.
 *
 * `supersedes` is the `versionId` the clinician was looking at, and it is required. The
 * server compares it with the note's latest version and refuses with
 * `NOTE_VERSION_STALE` if somebody else corrected it first — which is the only reason
 * this is not simply "write the next version".
 */
export async function amendNote(
  patientId: string,
  noteId: string,
  supersedes: string,
  submission: NoteSubmission,
  signal: AbortSignal,
): Promise<NoteWritten> {
  return post(
    `/api/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}/versions`,
    { ...submission, supersedes },
    signal,
  )
}

/**
 * One version of a note. Mirrors `NoteVersionView`.
 *
 * Wider than the overview's `PatientNote` in the two directions a history needs: the row
 * identity, and the signature that froze it.
 */
export interface NoteVersion {
  /** `clinical.note.version_id` — this exact row, and what a correction supersedes. */
  readonly versionId: string

  readonly version: number

  /** The `version_id` this version replaced, or null for version 1. */
  readonly supersedes: string | null

  /** `signed`, `amended`, `draft` or `entered-in-error`. */
  readonly status: string

  readonly noteType: string
  readonly language: string

  readonly subjective: string | null
  readonly objective: string | null
  readonly assessment: string | null
  readonly plan: string | null
  readonly bodyText: string | null

  /** The same on every version of one note — a correction inherits version 1's. */
  readonly effectiveAt: string

  /** When this version was written. What differs between versions. */
  readonly recordedAt: string

  readonly authorGivenName: string | null
  readonly authorFamilyName: string | null
  readonly authorRoleTitle: string | null

  /**
   * When this version was signed, or null when it is not signed.
   *
   * Null is a real state, not an error — everything below is null with it. A screen that
   * hid an unsigned version would hide what the record contains.
   */
  readonly signedAt: string | null

  /** The role the signer signed in, as a code. Never an authorisation decision. */
  readonly signerRole: string | null
  readonly signerGivenName: string | null
  readonly signerFamilyName: string | null
  readonly signerRoleTitle: string | null

  /** How the clock behind `signedAt` was known — an assertion about the box (#37). */
  readonly clockSource: string | null
  readonly clockUncertaintySeconds: number | null
}

/** A note's whole history. Mirrors `NoteVersions`. */
export interface NoteHistory {
  readonly noteId: string
  readonly patientId: string

  /** Newest first. Never empty — a note with no versions is a 404. */
  readonly versions: readonly NoteVersion[]
}

/** Why a history could not be read. */
export type NoteHistoryFailure = 'not-found' | 'unavailable'

/**
 * The history could not be read.
 *
 * A failure union rather than a status code, matching `PatientSearchError`: the screen
 * has two things to say, and which one is not a property of HTTP.
 */
export class NoteHistoryError extends Error {
  readonly failure: NoteHistoryFailure

  constructor(failure: NoteHistoryFailure) {
    super(`Note history unavailable: ${failure}`)
    this.name = 'NoteHistoryError'
    this.failure = failure
  }
}

/**
 * Every version of one note, newest first.
 *
 * **This is a read of the record and it writes an audit row** (AGENTS.md §4) — which is
 * why the editor asks for it when a clinician opens a correction rather than prefetching
 * it for every note on the screen. Thirty notes' histories fetched on page load would be
 * thirty rows claiming somebody read thirty notes, and no `DELETE` to take them back.
 *
 * Served on the note's own URL. `…/versions` answers identically — see `NoteEndpoints` —
 * and this uses the shorter one because it is the canonical address of a note that *is*
 * its versions.
 */
export async function fetchNoteVersions(
  patientId: string,
  noteId: string,
  signal: AbortSignal,
): Promise<NoteHistory> {
  let response: Response

  try {
    response = await fetch(
      `/api/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}`,
      { signal, headers: { Accept: 'application/json' } },
    )
  } catch (cause) {
    if (signal.aborted) {
      throw cause
    }
    throw new NoteHistoryError('unavailable')
  }

  if (response.status === 404) {
    throw new NoteHistoryError('not-found')
  }

  // Before the general case, because a 401 is not an unavailable box: it means the session
  // has ended, and `SessionGate` is about to show the login screen. Reported rather than
  // rendered — see `auth/sessionExpiry.ts`. The throw still happens, so this screen does not
  // sit half-updated in the moment before it unmounts.
  unauthenticated(response)

  if (!response.ok) {
    throw new NoteHistoryError('unavailable')
  }

  try {
    return (await response.json()) as NoteHistory
  } catch {
    throw new NoteHistoryError('unavailable')
  }
}

/**
 * The newest version, which is the one a correction supersedes.
 *
 * The server returns them newest first, and this does not rely on that: it takes the
 * highest `version` instead. An order the screen assumed and the server changed would
 * mean a correction quietly superseding version 1 of a note that has four, which the
 * server would then refuse with `NOTE_VERSION_STALE` — recoverable, but confusing for
 * a reason that would be invisible.
 */
export function currentVersion(history: NoteHistory): NoteVersion | null {
  return history.versions.reduce<NoteVersion | null>(
    (newest, candidate) =>
      newest === null || candidate.version > newest.version ? candidate : newest,
    null,
  )
}
